import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_URL from '../config/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || 'Request failed. Please try again.');
      }

      setMessage(data?.message || 'If the email exists, a password reset link has been sent.');
    } catch (err) {
      setError(err.message || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="glass rise-in" style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>ARMITA</h1>
          <p style={styles.tagline}>Reset your password</p>
        </div>

        {error && <div className="alert alert--danger" role="alert" style={styles.banner}>{error}</div>}
        {message && <div className="alert alert--success" role="alert" style={styles.banner}>{message}</div>}

        <div style={styles.content}>
          {!message && (
            <>
              <p style={styles.description}>
                Enter the email address associated with your account and we&apos;ll
                send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label className="field__label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="email@organization.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn--primary btn--block btn--lg"
                  style={styles.submit}
                  disabled={loading}
                >
                  {loading && <span className="spinner spinner--sm" aria-hidden="true" />}
                  {loading ? 'Please wait...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          <div style={styles.linkRow}>
            <Link to="/login" style={styles.link}>Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-canvas)',
    padding: 'var(--space-4)',
    boxSizing: 'border-box'
  },
  card: {
    padding: 'clamp(24px, 4vw, 36px)',
    width: '100%',
    maxWidth: '440px',
    maxHeight: 'min(92dvh, 92vh)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--space-5)',
    flexShrink: 0
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0
  },
  logo: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '0.22em'
  },
  tagline: {
    fontSize: '0.88rem',
    color: 'var(--text-secondary)',
    margin: '6px 0 0 0',
    letterSpacing: '-0.01em'
  },
  banner: {
    marginBottom: 'var(--space-3)',
    flexShrink: 0
  },
  description: {
    fontSize: '0.88rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
    margin: '0 0 var(--space-4) 0'
  },
  submit: {
    marginTop: 'var(--space-2)'
  },
  linkRow: {
    textAlign: 'center',
    marginTop: 'var(--space-4)'
  },
  link: {
    color: 'var(--accent)',
    fontSize: '0.85rem',
    textDecoration: 'none'
  }
};

export default ForgotPasswordPage;
